import React, { useEffect, useMemo, useState } from 'react'
import { CheckBox, Loader, NVButton } from '@nv/basic-components'
import { SearchBar } from '@nv/basic-components'
import DownloadFileIcon from '@nv/images/design/download-file-icon'
import DropdownArrowIcon from '@nv/images/design/dropdown-arrow-icon'
import FunnelIcon from '@nv/images/design/funnel-icon'
import RefreshIcon from '@nv/images/design/refresh-icon'
import TableSortIcon from '@nv/images/design/table-sort-icon'
import { flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table'
import clsx from 'clsx'
import _ from 'lodash'
import { useTranslation } from 'react-i18next'
import { ActionDropdown } from '../action_dropdown/ActionDropdown'
import Pagination from './components/pagination/Pagination'
import styles from './ListTable.module.scss'

export const ListTable = ({
  columns,
  data,
  currentPageNum = 0,
  dataLength = 0,
  loading = true,
  showFilter = false,
  onPageChange,
  download,
  setFilterModal,
  refreshList,
  expandedContent,
  numRowsPerPage = 10,
  paginationLabel = 'main.rows',
  noDataLabel = 'main.noData',
  storageKey,
  onNumRowsChange,
  multiRowActions = [],
  onTriggerSearch,
  searchableStrings,
  onSearch,
  searchPlaceholder = 'main.search',
  searchValue,
}: any) => {
  const { t } = useTranslation()
  const [sorting, setSorting] = useState(() => (storageKey ? JSON.parse(localStorage.getItem(storageKey)) : []))
  const [newNumRowsPerPage, setNewNumRowsPerPage] = useState(numRowsPerPage)
  const [expandedRows, setExpandedRows] = useState([])
  const [selectedRows, setSelectedRows] = useState([])
  const [searchInput = searchableStrings?.length ? {} : '', setSearchInput] = useState()
  const [selectedSearchField, setSelectedSearchField] = useState(searchableStrings?.[0] || '')

  const debouncedSearch = useMemo(
    () =>
      _.debounce((term) => {
        onSearch(term)
      }, 450),
    [],
  )

  const checkboxColumn = multiRowActions?.length
    ? [
        {
          id: 'select',
          header: () => (
            <CheckBox
              classes={{ container: styles['multi-actions-container'] }}
              field='select-all'
              checked={selectedRows.length === data.length && data.length > 0}
              onChange={() => {
                if (selectedRows.length === data.length) setSelectedRows([])
                else setSelectedRows(data.map((_, i) => i.toString()))
              }}
              role='checkbox'
              tabIndex={0}
              square={true}
            />
          ),
          cell: ({ row }) => (
            <CheckBox
              classes={{ container: styles['multi-actions-container'] }}
              field={`select-row-${row.id}`}
              checked={selectedRows.includes(row.id)}
              onChange={() => {
                if (selectedRows.includes(row.id)) setSelectedRows((prev) => prev.filter((id) => id !== row.id))
                else setSelectedRows((prev) => [...prev, row.id])
              }}
              role='checkbox'
              tabIndex={0}
              square={true}
            />
          ),
        },
      ]
    : []

  useEffect(() => {
    storageKey && localStorage.setItem(storageKey, JSON.stringify(sorting))
  }, [sorting])

  const table = useReactTable({
    data,
    columns: [...checkboxColumn, ...columns],
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  const toggleRowExpand = (rowId) => {
    setExpandedRows((prev) => {
      if (prev.includes(rowId)) return prev.filter((id) => id !== rowId)
      else return [...prev, rowId]
    })
  }

  return (
    <>
      <div className={styles['table-container']}>
        <div className={styles['actionbar']}>
          <div className={styles['search-container']}>
            {onSearch &&
              searchableStrings?.map(({ label, value, placeholder }) => (
                <SearchBar
                  key={value}
                  label={t(label)}
                  name={`search-${value}`}
                  placeholder={placeholder || label}
                  onChange={(e) => {
                    const searchValueMulti = e.target.value
                    setSearchInput((prev: any) => ({ ...prev, [value]: searchValueMulti }))
                    debouncedSearch((prev: any) => ({ ...prev, [value]: searchValueMulti }))
                  }}
                  style={{ container: { width: 275, marginRight: 25 } }}
                  value={searchInput?.[value] || ''}
                />
              ))}

            {onSearch && !searchableStrings?.length && (
              <SearchBar
                placeholder={t(`${searchPlaceholder}`)}
                name='search'
                onChange={(e) => {
                  setSearchInput(e.target.value)
                  debouncedSearch(e.target.value)
                }}
                style={{ container: { width: 275, marginRight: 25 } }}
                value={searchInput || ''}
                label={t(`${searchPlaceholder}`)}
              />
            )}
          </div>

          <div className={styles['actionbar__icons']}>
            {multiRowActions?.length > 0 && selectedRows.length >= 2 && (
              <div className={styles['multi-actions']}>
                <ActionDropdown
                  values={multiRowActions.map((action) => ({
                    label: action.label,
                    value: action.value,
                  }))}
                  onCallback={async (selected) => {
                    const selectedData = selectedRows.map((id) => data[parseInt(id, 10)])
                    const selectedIds = selectedData.map((a) => a._id)

                    // console.log('Selected Row Data:', selectedData)
                    // console.log('Selected IDs:', selectedIds)
                    // console.log('Selected Action Value:', selected)

                    const action = multiRowActions.find((action) => action.value === selected)

                    if (action?.callback) {
                      try {
                        // console.log(`Calling function for action "${selected}" with IDs:`, selectedIds)
                        await action.callback(selectedIds)
                      } catch (err) {
                        console.error('Multi action error:', err)
                      }
                    } else {
                      console.warn(`No callback found for action: ${selected}`)
                    }
                  }}
                  ariaLabel={t('admin.dropdown.actionOptions', { value: 'selected rows' })}
                />
              </div>
            )}

            {refreshList && (
              <NVButton
                variant='iconOnly'
                icon={<RefreshIcon />}
                onClick={refreshList}
                ariaLabel={t('buttons.refresh')}
                classes={{ container: styles['refresh-btn'] }}
              />
            )}
            {showFilter && (
              <NVButton variant='iconOnly' icon={<FunnelIcon />} onClick={() => setFilterModal(true)} ariaLabel={t('buttons.filters')} />
            )}
            {download && (
              <NVButton variant='iconOnly' icon={<DownloadFileIcon />} onClick={() => download()} ariaLabel={t('buttons.download')} />
            )}
          </div>
        </div>

        {loading ? (
          <div className='spinner-container'>
            <Loader />
          </div>
        ) : !data.length ? (
          <div className={styles['empty-list']} role='alert' aria-live='assertive'>
            {t(noDataLabel)}
          </div>
        ) : (
          <div className={styles['table-scroller']}>
            <table className={styles['table']}>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      if (header.id === 'expand') return <td key={header.id} aria-hidden='true' />
                      return (
                        <th
                          key={header.id}
                          onClick={storageKey ? header.column.getToggleSortingHandler() : null}
                          className={clsx(styles['sortable-header'], {
                            [styles['action-col']]: header.column.id === 'action',
                            [styles['checkbox-col']]: header.column.id === 'select',
                          })}
                        >
                          {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getIsSorted() === 'asc' && <TableSortIcon className={styles['sort-asc']} />}
                          {header.column.getIsSorted() === 'desc' && <TableSortIcon className={styles['sort-desc']} />}
                        </th>
                      )
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <React.Fragment key={row.id}>
                    <tr className={styles['tr']}>
                      {row.getVisibleCells().map((cell: any) =>
                        cell.getContext().column.id !== 'expand' ? (
                          <React.Fragment key={cell.id}>
                            <th className={styles['mobile-header']}>{flexRender(cell.column.columnDef.header, cell.getContext())}</th>
                            <td
                              key={cell.id}
                              className={clsx(styles['wrap-column'], {
                                [styles['checkbox-col']]: cell.column.id === 'select',
                              })}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          </React.Fragment>
                        ) : (
                          <React.Fragment key={cell.id}>
                            <th className={clsx(styles['mobile-header'], styles['expand-move'])}>
                              {flexRender(cell.column.columnDef.header, cell.getContext())}
                            </th>
                            <td key={`expand-btn-${row.id}`} className={styles['expand-move']}>
                              <NVButton
                                onClick={() => toggleRowExpand(row.id)}
                                icon={
                                  <DropdownArrowIcon
                                    className={clsx({ [styles['expand-btn__expanded']]: expandedRows.includes(row.id) })}
                                  />
                                }
                                classes={{ container: styles['expand-btn'] }}
                                ariaLabel={expandedRows.includes(row.id) ? 'Collapse row' : 'Expand row'}
                              />
                            </td>
                          </React.Fragment>
                        ),
                      )}
                    </tr>

                    {expandedRows.includes(row.id) && (
                      <tr>
                        <td colSpan={row.getVisibleCells().length + 1} className={styles['expanded__content']}>
                          {expandedContent(row)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>

              <tfoot>
                {table.getFooterGroups().some((footerGroup) => footerGroup.headers.some((header) => header.column.columnDef.footer)) &&
                  table.getFooterGroups().map((footerGroup) => (
                    <tr key={footerGroup.id}>
                      {footerGroup.headers.map((header) => (
                        <th key={header.id}>
                          {header.isPlaceholder ? null : flexRender(header.column.columnDef.footer, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {dataLength !== 0 && (
        <Pagination
          currentPageNum={currentPageNum}
          dataLength={dataLength}
          onPageChange={onPageChange}
          numRowsPerPage={newNumRowsPerPage}
          paginationLabel={paginationLabel}
          setNewNumRowsPerPage={setNewNumRowsPerPage}
          onNumRowsChange={onNumRowsChange}
        />
      )}
    </>
  )
}
